import { useState } from 'react';
import {
    Navigate,
    createSearchParams,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import { useTelemetryContext } from '@myphx-engineering/instrumentation/react';
import { useGeolocation } from '@myphx-engineering/location-utils';
import { useConditionalBackOverride } from '@myphx-engineering/phx-design-system';

import { ShoppingHeader } from '../../components/cabinet/shopping-header/ShoppingHeader';
import { DrugFamilyHeader } from '../../components/common/DrugFamilyHeader';
import { ProductConfiguration } from '../../components/product-configuration';
import { ProviderSelection } from '../../components/provider-offers';
import { getFragment, getFragments } from '../../graphql';
import {
    GetProductOffersDocument,
    type GetProductOffersQuery,
} from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import { useNumericSearchParam } from '../../hooks/use-numeric-search-param';
import { usePatientContext } from '../../hooks/use-patient-context';
import { type DataHandlerType, QueryLoader } from '../../loaders';
import { getAbsoluteRoute } from '../../util';
import { getFeaturedProviderOffers } from '../../util/alternatives/get-featured-provider-offers';
import { getOffersByProviderId } from '../../util/offers/get-offers-by-provider-id';
import {
    getPrimaryProvider,
    getProviderOffersByType,
} from '../../util/provider-offers/provider-utils';
import { usePrescriptionInputVariables } from '../cabinet/prescription/use-prescription-variables';
import { MedicationRoute } from '../medication/MedicationRoute';

export const DrugRoute = () => {
    const { typeaheadSearchEnabled } = useFlags();

    if (typeaheadSearchEnabled) {
        return <MedicationRoute />;
    }

    return <DrugRouteV1 />;
};

const DrugRouteV1 = () => {
    const { primaryPatientId: patientId } = usePatientContext();

    const distance = useNumericSearchParam('within');
    const chosenQuantity = useNumericSearchParam('quantity');
    const daysSupply = useNumericSearchParam('daysSupply');

    const Loader = QueryLoader<typeof GetProductOffersDocument>;
    const { drugId } = useParams();

    const input = usePrescriptionInputVariables(distance, patientId);

    if (!drugId) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    return (
        <Loader
            query={GetProductOffersDocument}
            variables={{
                productId: drugId,
                input: {
                    quantity: chosenQuantity,
                    daysSupply: daysSupply,
                    ...input,
                },
            }}
            component={DataHandler}
        />
    );
};

const DataHandler: DataHandlerType<typeof GetProductOffersDocument> = ({
    data,
}: {
    data: GetProductOffersQuery;
}) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isDrugConfigOpen, setDrugConfigOpen] = useState(false);
    const { isAuthenticated, signIn } = useAuthContext();
    const location = useLocation();
    const { geolocation } = useGeolocation();

    const searchQuery = searchParams.get('q');
    const quantityInput = searchParams.get('qInput');
    const quantity = searchParams.get('quantity');

    const handleBack = () => {
        if (searchQuery) {
            navigate({
                pathname: getAbsoluteRoute('drug.search.root'),
                search: createSearchParams({ q: searchQuery }).toString(),
            });
        } else {
            navigate(getAbsoluteRoute('drug.search.root'));
        }
    };

    useConditionalBackOverride(handleBack, !!searchQuery);

    const { telemetryInstance } = useTelemetryContext();
    const product = getFragment(data.product);
    const offersConnection = getFragment(data.product?.offersConnection);

    if (!product || !offersConnection) {
        telemetryInstance.logError({
            source: 'drug-route',
            error: new Error('no data'),
        });
        return <Navigate to={getAbsoluteRoute('error')} />;
    }
    const providerOffers = offersConnection.edges.map((edge) =>
        getFragment(edge.node)
    );

    const onSelect = (providerOffersId: string, providerId?: string) => {
        if (!isAuthenticated) {
            signIn(`${location.pathname}${location.search}`);
            return;
        }

        const selectedProviderOffers = providerOffers.find(
            (offer) => offer.id === providerOffersId
        );

        const chosenQuantity =
            quantityInput ?? quantity ?? String(product.modeQuantity);

        const newSearchParams = providerId
            ? createSearchParams({
                  providerId,
                  qInput: chosenQuantity,
              }).toString()
            : createSearchParams({
                  qInput: chosenQuantity,
              }).toString();

        if (selectedProviderOffers) {
            //Skip to overview if only one offer option.

            if (
                selectedProviderOffers?.provider.__typename === 'ChainProvider'
            ) {
                const selectedProviderId =
                    providerId ??
                    getPrimaryProvider(selectedProviderOffers, geolocation)?.id;

                if (selectedProviderId) {
                    const offers = getFragments(selectedProviderOffers.offers);

                    const offersForSelected = getOffersByProviderId(
                        offers,
                        selectedProviderId
                    );

                    if (offersForSelected.length <= 1) {
                        navigate({
                            pathname: getAbsoluteRoute('transfer.root', {
                                providerOffersId,
                                offerId: offersForSelected[0].id,
                            }),
                            search: newSearchParams,
                        });
                        return;
                    }
                }
            }

            if (selectedProviderOffers.offers.length <= 1) {
                const offerFragment = getFragment(
                    selectedProviderOffers.offers[0]
                );

                navigate({
                    pathname: getAbsoluteRoute('transfer.root', {
                        providerOffersId,
                        offerId: offerFragment?.id ?? 'unknown',
                    }),
                    search: newSearchParams,
                });
                return;
            }
        }

        navigate({
            pathname: getAbsoluteRoute('drug.drugId.providerOffers.root', {
                providerOffersId,
                drugId: product.id,
            }),
            search: newSearchParams,
        });
    };

    const featuredProvider = getFeaturedProviderOffers(offersConnection);

    const providerOfferTypes = getProviderOffersByType(
        offersConnection,
        featuredProvider?.providerOffers.id
    );

    const controlledSubstanceCode = offersConnection.edges[0]
        ? getFragment(getFragment(offersConnection.edges[0].node)?.medication)
              ?.controlledSubstanceCode
        : null;

    return isDrugConfigOpen ? (
        <ProductConfiguration
            drugId={product.id}
            drugFamilyId={searchParams.get('familyId') ?? undefined}
            closeEdit={() => setDrugConfigOpen(false)}
        />
    ) : (
        <ProviderSelection
            providerOffersByType={providerOfferTypes}
            onSelect={onSelect}
            header={
                <ShoppingHeader>
                    <DrugFamilyHeader
                        controlledSubstanceCode={controlledSubstanceCode}
                        drugId={product.id}
                        editClicked={() => {
                            setDrugConfigOpen(true);
                        }}
                    />
                </ShoppingHeader>
            }
            featuredProvider={featuredProvider}
        />
    );
};
